<template>
  <WdsStack justify-space-between class="tw-mx-1 tw-pb-1.5">
    <div>
      <WdsStack justify-start gap-sm align-center>
        <WdsInput
          type="text"
          class="tw-min-w-[320px]"
          default
          prefix-icon="search"
          :placeholder="$t('Search name, email, skills...')"
          data-test="tc-search-input"
          @update:model-value="(value) => $emit('search', value)"
        />
        <WdsSelect
          v-model="filters.skills"
          multi-select
          can-deselect
          searchable
          :disabled="skillsLoading"
          :list="skillsOptions"
          :load-more-distance="20"
          :placeholder="$t('Select skills')"
          class="tw-w-[300px]"
          data-test="tc-filter-skills"
          @load-more="loadMoreSkills"
        >
          <template #option="{ option }">
            <div :data-test="`filter-skill-${option.label.toLowerCase()}`">
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <DataTableSort :sort-options="sortOptions" @sort="(value) => $emit('sort', value)" />
      </WdsStack>
    </div>
  </WdsStack>
</template>

<script setup>
import { ref, defineEmits, watch, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsInput, WdsStack, WdsSelect } from '@worksome/wds-components'
import unionBy from 'lodash/unionBy'
import { $t } from '@/composables/translation'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { TrustedContactsSkillsFilterData } from '@/components/Contacts/CompanyFreelancer/queries/TrustedContacts'
import { merge } from '@/utils/tools'
import DataTableSort from '@/components/BaseDataTable/DataTableSort.vue'
import { SortOrder, TrustedContactOrderByColumn } from '@/gql/graphql'

const emit = defineEmits(['search', 'update-filters', 'sort'])

const skillsVariables = ref({
  statuses: ['APPLIED'],
  page: 1,
  first: PER_PAGE_MAXIMUM,
  accounts: [currentAccount.globalId],
})

const {
  result: skillsResult,
  loading: skillsLoading,
  fetchMore: fetchMoreSkills,
} = useQuery(TrustedContactsSkillsFilterData, skillsVariables, {
  clientId: 'publicApi',
})

const loadMoreSkills = () => {
  if (skillsLoading.value) return
  const { hasMorePages, currentPage } = skillsResult?.value?.trustedContacts.paginatorInfo ?? {}
  if (!hasMorePages) return

  fetchMoreSkills({
    variables: {
      page: currentPage + 1,
    },
    updateQuery(prev, { fetchMoreResult }) {
      return merge(prev, fetchMoreResult)
    },
  })
}

const sortByLabel = (itemA, itemB) => itemA.label.trim().localeCompare(itemB.label.trim())

const skillsOptions = computed(() => {
  const skills =
    skillsResult.value?.trustedContacts?.data?.reduce((trustedContactAcc, trustedContact) => {
      const trustedContactSkills = unionBy(
        trustedContact.worker?.skills,
        trustedContact.skills,
        'value',
      ).reduce(
        (trustedContactSkillsAcc, { label, value }) => ({
          ...trustedContactSkillsAcc,
          [value]: { label, value },
        }),
        {},
      )

      return {
        ...trustedContactAcc,
        ...trustedContactSkills,
      }
    }, {}) ?? {}

  return skills ? Object.values(skills).sort(sortByLabel) : []
})

const sortOptions = [
  {
    name: 'name_asc',
    primaryText: $t('Name'),
    secondaryText: $t('A to Z'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'name_desc',
    primaryText: $t('Name'),
    secondaryText: $t('Z to A'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'date_added_desc',
    primaryText: $t('Date applied'),
    secondaryText: $t('New to old'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'date_added_asc',
    primaryText: $t('Date applied'),
    secondaryText: $t('Old to new'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'jobs_taken_desc',
    primaryText: $t('Jobs Taken'),
    secondaryText: $t('More to less'),
    orderByColumn: TrustedContactOrderByColumn.WorkerJobsCount,
    sortOrder: SortOrder.Desc,
  },
]

const filters = ref({
  statuses: [],
})

watch(filters.value, (updatedFilters) => {
  emit('update-filters', {
    skills: updatedFilters.skills.map(({ value }) => value),
  })
})
</script>

<style scoped lang="scss">
:deep(.wds-input-label) {
  margin-bottom: 0 !important;
}
</style>
